import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/DropdownMenu";
import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/utils";

import { useCartCount } from "../cart/store";
import { usePublicCategories } from "../catalog/api";
import { SearchDropdown } from "../catalog/SearchDropdown";
import { useAuth } from "../auth/AuthContext";

// FR-1 — sticky header on every public page. Desktop shows logo +
// categories dropdown + search + cart + sign-in. Mobile (< 768px)
// collapses to hamburger + logo + cart, with category nav inside a
// drawer. The header steps out of sticky mode when the iOS keyboard
// opens so the search input is not covered (FR-1 / FR-8).
export function NavHeader(): JSX.Element {
  const sticky = useStickyWhenKeyboardClosed();
  const [mobileOpen, setMobileOpen] = useState(false);
  const scrolled = useScrolledPastTop();

  return (
    <header
      className={cn(
        "relative border-b border-zinc-200 bg-brand-white transition-shadow",
        sticky ? "sticky top-0 z-30" : "",
        // Phase 4e brand-visual-rollout (take 2) — drop shadow appears
        // once the user scrolls past the top, lifting the header off
        // the hero. Subtle, professional.
        scrolled ? "shadow-md shadow-flag-navy/10" : ""
      )}
      role="banner"
    >
      {/* Phase 4e brand-visual-rollout — brand-red hairline ties the
          header to the hero / pre-footer / footer top accents. */}
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-0.5 bg-brand-red" />
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-3 py-2 md:gap-4 md:px-6 md:py-3">
        <button
          type="button"
          className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-md text-brand-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red"
          aria-label="Open menu"
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav"
          onClick={() => setMobileOpen((v) => !v)}
        >
          <BarsIcon />
        </button>
        <Link
          to="/"
          className="inline-flex items-center"
          aria-label="JDR Equipment Rentals — home"
        >
          {/* Phase 4e brand-visual-rollout FR-8 — desktop renders the
              full wordmark+banner web logo at 48px tall; mobile swaps
              to the square monogram at 40px tall. Both alt-text the
              same string so AC-2's alt query catches both. */}
          <img
            src="/brand/jdr-logo-web.png"
            srcSet="/brand/jdr-logo-web.png 1x, /brand/jdr-logo-web@2x.png 2x"
            alt="JDR Equipment Rentals"
            height={48}
            className="hidden h-12 w-auto md:block"
          />
          <img
            src="/brand/jdr-monogram-180.png"
            alt="JDR Equipment Rentals"
            height={40}
            className="h-10 w-auto md:hidden"
          />
        </Link>

        <nav aria-label="Browse" className="hidden md:flex">
          <CategoriesMenu />
        </nav>

        <div className="ml-auto flex items-center gap-2 md:gap-3">
          <div className="hidden md:block">
            <SearchDropdown />
          </div>
          <Link
            to="/cart"
            className="relative inline-flex h-10 w-10 items-center justify-center rounded-md text-brand-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red"
            aria-label="View cart"
          >
            <CartIcon />
            <CartBadge />
          </Link>
          <AuthMenu />
        </div>
      </div>

      {mobileOpen ? (
        <MobileDrawer id="mobile-nav" onClose={() => setMobileOpen(false)} />
      ) : null}
    </header>
  );
}

// Desktop nav slot: shows "Sign in" when anonymous, account dropdown
// when authenticated. Phase 2a customer-accounts FR-7.
function AuthMenu(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();

  if (!auth.user) {
    return (
      <Link
        to="/sign-in"
        className="hidden md:inline-flex h-10 items-center px-3 text-sm font-medium text-brand-black hover:text-brand-red focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red rounded"
      >
        Sign in
      </Link>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="hidden md:inline-flex h-10 items-center gap-1 rounded px-3 text-sm font-medium text-brand-black hover:text-brand-red focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red"
        >
          <span className="max-w-[10rem] truncate">{auth.user.email}</span>
          <ChevronDownIcon />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem asChild>
          <Link to="/portal">Dashboard</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/portal/profile">Your account</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/portal/profile#addresses">Addresses</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/portal/profile#security">Security</Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            void auth.signOut().then(() => navigate("/"));
          }}
        >
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CartBadge(): JSX.Element | null {
  const count = useCartCount();
  if (count === 0) return null;
  return (
    <Badge
      variant="danger"
      className="absolute -right-1 -top-1 min-w-[1.25rem] justify-center px-1"
      aria-label={`${count} items in cart`}
    >
      {count > 99 ? "99+" : count}
    </Badge>
  );
}

function CategoriesMenu(): JSX.Element {
  const { data } = usePublicCategories();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex h-10 items-center gap-1 rounded px-3 text-sm font-medium text-brand-black hover:text-brand-red focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red"
        >
          Categories
          <ChevronDownIcon />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {(data ?? []).map((c) => (
          <DropdownMenuItem key={c.id} asChild>
            <Link to={`/c/${c.slug}`}>{c.name}</Link>
          </DropdownMenuItem>
        ))}
        {(data ?? []).length === 0 ? (
          <DropdownMenuItem disabled>Loading…</DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileDrawer({ id, onClose }: { id: string; onClose: () => void }): JSX.Element {
  const { data } = usePublicCategories();
  const navigate = useNavigate();
  const auth = useAuth();
  const [query, setQuery] = useState("");

  return (
    <div id={id} className="border-t border-zinc-200 bg-brand-white px-3 py-3 md:hidden">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (query.trim().length === 0) return;
          navigate(`/search?q=${encodeURIComponent(query.trim())}`);
          onClose();
        }}
        className="mb-3 flex gap-2"
      >
        <Input
          type="search"
          aria-label="Search equipment"
          placeholder="Search equipment"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Button type="submit" size="sm">Search</Button>
      </form>
      <ul className="divide-y divide-zinc-100">
        {(data ?? []).map((c) => (
          <li key={c.id}>
            <Link
              to={`/c/${c.slug}`}
              onClick={onClose}
              className="flex h-11 items-center justify-between text-sm text-brand-black"
            >
              <span>{c.name}</span>
              <ChevronRightIcon />
            </Link>
          </li>
        ))}
        {auth.user ? (
          <>
            <li>
              <Link
                to="/portal/profile"
                onClick={onClose}
                className="flex h-11 items-center justify-between text-sm text-brand-black"
              >
                <span>Your account</span>
                <ChevronRightIcon />
              </Link>
            </li>
            <li>
              <button
                type="button"
                onClick={() => {
                  void auth.signOut().then(() => {
                    onClose();
                    navigate("/");
                  });
                }}
                className="flex h-11 w-full items-center justify-between text-sm text-brand-black"
              >
                <span>Sign out</span>
                <ChevronRightIcon />
              </button>
            </li>
          </>
        ) : (
          <li>
            <Link
              to="/sign-in"
              onClick={onClose}
              className="flex h-11 items-center justify-between text-sm text-brand-black"
            >
              <span>Sign in</span>
              <ChevronRightIcon />
            </Link>
          </li>
        )}
      </ul>
    </div>
  );
}

// Lifts a drop shadow under the sticky header once the user scrolls
// off the top of the page. Pure-CSS sticky shadows don't degrade
// gracefully, so we read scrollY directly. Single passive listener;
// throttled by requestAnimationFrame.
function useScrolledPastTop(): boolean {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let raf = 0;
    const onScroll = (): void => {
      if (raf !== 0) return;
      raf = window.requestAnimationFrame(() => {
        setScrolled(window.scrollY > 8);
        raf = 0;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf !== 0) window.cancelAnimationFrame(raf);
    };
  }, []);

  return scrolled;
}

// On iOS, when the virtual keyboard opens, the `visualViewport.height`
// shrinks. The header keeps `sticky` only while the keyboard is closed
// so the focused address/search input isn't covered. Desktop browsers
// stay sticky always (no visualViewport mutation on focus).
function useStickyWhenKeyboardClosed(): boolean {
  const baselineRef = useRef<number | null>(null);
  const [sticky, setSticky] = useState(true);

  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return undefined;
    baselineRef.current = vv.height;
    const onResize = (): void => {
      const baseline = baselineRef.current ?? vv.height;
      // 150px is a generous threshold to ignore browser-chrome shrinking
      // (URL bar collapse) and only trip on the much larger keyboard.
      setSticky(vv.height >= baseline - 150);
    };
    vv.addEventListener("resize", onResize);
    return () => vv.removeEventListener("resize", onResize);
  }, []);

  return sticky;
}

function BarsIcon(): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function CartIcon(): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M2 3h2l2 11h10l2-7H6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="17" r="1.2" fill="currentColor" />
      <circle cx="15" cy="17" r="1.2" fill="currentColor" />
    </svg>
  );
}

function ChevronDownIcon(): JSX.Element {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRightIcon(): JSX.Element {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M5 3l3 3-3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
